import { useEffect, useRef } from 'react';

export const DEFAULT_SANDSTORM_CONFIG = {
  count:          1024,
  color:          '#f0ae44',
  windSpeed:      160,
  turbulence:     70,
  gustCycle:      2.5,
  gustAmplitude:  0.10,
  verticalDrift:  35,
  sizeMin:        5,
  sizeMax:        10,
  opacityMin:     0.12,
  opacityMax:     0.65,
  mouseRadius:     30,
  mouseStrength:   730,
  mouseEnabled:    true,   // mirrors Home.jsx mouseEnabled state
  mouseSwirlRatio: 0,     // tangential swirl around cursor
  mouseBounce:     0.9,   // restitution coefficient (0 = slide, 1 = elastic)
  // --- Fluid dynamics ---
  enableFlows:    true,
  numFlows:       4,      // 1-4 active thermals
  flowStrength:   200,    // thermal updraft/downdraft speed (px/s)
  vortexStrength: 130,    // Rankine circulation tangential speed (px/s)
  flowRadius:     170,    // Gaussian sigma for thermal influence (px)
  flowDrift:      14,     // drift speed of thermal centers (px/s)
  easterEggEnabled: false, // set true when debug panel is open
};

const vertexShader = /* glsl */`
  attribute float aSize;
  attribute float aAlpha;
  varying float vAlpha;
  void main() {
    vAlpha = aAlpha;
    gl_PointSize = aSize;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const fragmentShader = /* glsl */`
  precision mediump float;
  uniform vec3 uColor;
  varying float vAlpha;
  void main() {
    float d = length(gl_PointCoord - vec2(0.5));
    if (d > 0.5) discard;
    float soft = 1.0 - smoothstep(0.2, 0.5, d);
    gl_FragColor = vec4(uColor, vAlpha * soft);
  }
`;

// Initialize one thermal cell at a random position on screen
function mkFlow(w, h) {
  const angle = Math.random() * Math.PI * 2;
  const hot   = Math.random() > 0.5;
  return {
    x:          (Math.random() - 0.5) * w,
    y:          (Math.random() - 0.5) * h,
    hot,
    sign:       hot ? 1 : -1,            // +1 = hot (up), -1 = cold (down)
    vortexSign: Math.random() > 0.5 ? 1 : -1,  // CCW (+1) or CW (−1)
    driftX:     Math.cos(angle),          // unit drift direction
    driftY:     Math.sin(angle),
  };
}

// Props:
//   configRef — React ref; .current is read every tick (no re-render cost)
//   mouseRef  — React ref { x, y, enabled } in viewport px; optional
//   resetRef  — React ref; .current set to a reset() function once Three.js loads
//   onStats   — callback({ fps, frameMs }) every 20 frames; optional
export function SandstormBackground({ configRef, mouseRef, resetRef, onStats, onAllCaptured }) {
  const mountRef = useRef(null);

  useEffect(() => {
    const el = mountRef.current;
    if (!el) return;

    let animId;
    let cleanup = () => {};
    let active = true;

    // Lazy-load Three.js → separate webpack chunk, content-hash cached
    import('three').then((THREE) => {
      if (!active) return;

      const cfg0 = configRef.current;
      let w = el.clientWidth;
      let h = el.clientHeight;

      // --- Renderer ---
      const renderer = new THREE.WebGLRenderer({ antialias: false, alpha: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.setSize(w, h);
      el.appendChild(renderer.domElement);

      // --- Orthographic camera: pixel-space, center = origin ---
      const camera = new THREE.OrthographicCamera(-w/2, w/2, h/2, -h/2, 0.1, 10);
      camera.position.z = 1;

      const scene = new THREE.Scene();

      // --- Per-particle CPU buffers ---
      const n   = cfg0.count;
      const pos = new Float32Array(n * 3); // x, y, z
      const vx  = new Float32Array(n);     // base horizontal velocity (px/s)
      const vy  = new Float32Array(n);     // base vertical velocity (px/s)
      const sz  = new Float32Array(n);     // point size (px)
      const al  = new Float32Array(n);     // alpha

      // Easter egg: per-particle orbit tracking for black hole capture
      const captured    = new Uint8Array(n);    // 1 = captured (hidden off-screen)
      const orbitAngle  = new Float32Array(n);  // last recorded angle to cursor
      const orbitAcc    = new Float32Array(n);  // accumulated orbit angle (radians)
      const orbitInZone = new Uint8Array(n);    // 1 = was inside tracking zone last frame
      let   capturedCount    = 0;
      let   allCapturedFired = false;

      function initParticle(i, cfg, scattered) {
        const pi = i * 3;
        pos[pi]   = scattered ? (Math.random() - 0.5) * w : -w / 2 - 8;
        pos[pi+1] = (Math.random() - 0.5) * h;
        pos[pi+2] = 0;
        vx[i] = cfg.windSpeed * (0.4 + Math.random() * 0.6);
        vy[i] = (Math.random() - 0.5) * cfg.verticalDrift;
        sz[i] = cfg.sizeMin + Math.random() * (cfg.sizeMax - cfg.sizeMin);
        al[i] = cfg.opacityMin + Math.random() * (cfg.opacityMax - cfg.opacityMin);
      }

      for (let i = 0; i < n; i++) initParticle(i, cfg0, true);

      // --- GPU geometry (single draw call) ---
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
      geo.setAttribute('aSize',    new THREE.BufferAttribute(sz,  1));
      geo.setAttribute('aAlpha',   new THREE.BufferAttribute(al,  1));

      const mat = new THREE.ShaderMaterial({
        vertexShader,
        fragmentShader,
        uniforms: { uColor: { value: new THREE.Color(cfg0.color) } },
        transparent: true,
        depthWrite: false,
      });

      scene.add(new THREE.Points(geo, mat));

      // --- Cross burst system (particle capture flash) ---
      const CROSS_ARM  = 5;
      const CROSS_PTS  = 5;   // center + 4 arm tips
      const CROSS_FADE = 2.5; // alpha/s
      const crossPos   = new Float32Array(n * CROSS_PTS * 3);
      const crossSz    = new Float32Array(n * CROSS_PTS).fill(2.5);
      const crossAl    = new Float32Array(n * CROSS_PTS);
      const crossAlpha = new Float32Array(n);
      const crossLive  = new Uint8Array(n);
      const crossFree  = new Int32Array(n);
      for (let k = 0; k < n; k++) crossFree[k] = k;
      let crossFreeTop = n;

      function spawnCross(cx, cy) {
        if (crossFreeTop === 0) return;
        const s  = crossFree[--crossFreeTop];
        crossLive[s]  = 1;
        crossAlpha[s] = 1.0;
        const bi = s * CROSS_PTS;
        const bpi = bi * 3;
        const arms = [cx, cy, 0, cx - CROSS_ARM, cy, 0, cx + CROSS_ARM, cy, 0, cx, cy - CROSS_ARM, 0, cx, cy + CROSS_ARM, 0];
        for (let k = 0; k < 15; k++) crossPos[bpi + k] = arms[k];
        for (let k = 0; k < CROSS_PTS; k++) crossAl[bi + k] = 1.0;
      }

      const geoX = new THREE.BufferGeometry();
      geoX.setAttribute('position', new THREE.BufferAttribute(crossPos, 3));
      geoX.setAttribute('aSize',    new THREE.BufferAttribute(crossSz, 1));
      geoX.setAttribute('aAlpha',   new THREE.BufferAttribute(crossAl, 1));
      const matX = new THREE.ShaderMaterial({
        vertexShader, fragmentShader,
        uniforms: { uColor: { value: new THREE.Color(cfg0.color) } },
        transparent: true, depthWrite: false,
      });
      scene.add(new THREE.Points(geoX, matX));

      // --- Thermal flow cells (max 4, indexed by numFlows in config) ---
      const MAX_FLOWS = 4;
      const flows = Array.from({ length: MAX_FLOWS }, () => mkFlow(w, h));

      // --- Expose reset function to parent via ref ---
      if (resetRef) {
        resetRef.current = () => {
          const cfg = configRef.current;
          captured.fill(0);
          orbitAcc.fill(0);
          orbitAngle.fill(0);
          orbitInZone.fill(0);
          capturedCount    = 0;
          allCapturedFired = false;
          crossLive.fill(0);
          crossAlpha.fill(0);
          crossAl.fill(0);
          for (let k = 0; k < n; k++) crossFree[k] = k;
          crossFreeTop = n;
          geoX.attributes.aAlpha.needsUpdate = true;
          for (let i = 0; i < n; i++) initParticle(i, cfg, true);
          geo.attributes.position.needsUpdate = true;
          geo.attributes.aSize.needsUpdate    = true;
          geo.attributes.aAlpha.needsUpdate   = true;
          for (let fi = 0; fi < MAX_FLOWS; fi++) {
            flows[fi] = mkFlow(w, h);
          }
        };
      }

      // --- Animation state ---
      let last      = performance.now();
      let lastColor = cfg0.color;
      let frameN    = 0;
      const fpsBuf  = [];

      function tick(now) {
        animId = requestAnimationFrame(tick);

        fpsBuf.push(now);
        if (fpsBuf.length > 90) fpsBuf.shift();

        const dt  = Math.min((now - last) * 0.001, 0.05); // s, capped 50ms
        last = now;
        frameN++;

        const cfg = configRef.current;
        const t0  = performance.now();

        // Release captured particles when easter egg is disabled mid-capture
        if (!cfg.easterEggEnabled && capturedCount > 0) {
          for (let j = 0; j < n; j++) {
            if (captured[j]) { captured[j] = 0; initParticle(j, cfg, true); }
          }
          capturedCount    = 0;
          allCapturedFired = false;
          orbitAcc.fill(0);
          orbitInZone.fill(0);
          geo.attributes.position.needsUpdate = true;
          geo.attributes.aSize.needsUpdate    = true;
          geo.attributes.aAlpha.needsUpdate   = true;
        }

        // Sync color uniform lazily
        if (cfg.color !== lastColor) {
          mat.uniforms.uColor.value.set(cfg.color);
          matX.uniforms.uColor.value.set(cfg.color);
          lastColor = cfg.color;
        }

        // Gust multiplier
        const gust = 1 + cfg.gustAmplitude
          * Math.sin(now * 0.001 * (2 * Math.PI / cfg.gustCycle));

        // Mouse in world space (y-axis flipped for Three.js)
        const mouse   = mouseRef?.current;
        const mouseOn = cfg.mouseEnabled ?? false;
        const mx      = mouseOn ? (mouse.x ?? 0) - w / 2 : 0;
        const my      = mouseOn ? -((mouse.y ?? 0) - h / 2) : 0;
        const mr      = cfg.mouseRadius;
        const mr2     = mr * mr;
        // Velocity drag: EMA-smoothed velocity from Home.jsx + exponential time-decay
        // so dragging leaves a fading trail and the field dies when mouse is still.
        const mAge    = mouseOn ? Math.max(0, now - (mouse.lastTime ?? now)) * 0.001 : 0;
        const velFade = Math.exp(-mAge * 8); // ~0.4 s to reach ≈0
        const mvx     = mouseOn ? (mouse.vx ?? 0) * velFade : 0;
        const mvy     = mouseOn ? -((mouse.vy ?? 0) * velFade) : 0; // flip Y for Three.js

        // --- Update thermal flow positions (once per frame) ---
        const flowsOn  = cfg.enableFlows;
        const numF     = cfg.numFlows | 0; // integer
        const fStr     = cfg.flowStrength;
        const vStr     = cfg.vortexStrength;
        const R        = cfg.flowRadius;
        const R2i      = 1 / (R * R);
        const R2skip   = R * R * 9; // skip if particle > 3σ away (gauss < 0.012)
        const rc       = R * 0.25;  // Rankine core radius = 25% of thermal radius
        const drift    = cfg.flowDrift * dt;
        const hw       = w * 0.45, hh = h * 0.45; // soft-bounce boundaries

        if (flowsOn) {
          for (let fi = 0; fi < numF; fi++) {
            const f = flows[fi];
            f.x += f.driftX * drift;
            f.y += f.driftY * drift;
            if (f.x >  hw) f.driftX = -Math.abs(f.driftX);
            if (f.x < -hw) f.driftX =  Math.abs(f.driftX);
            if (f.y >  hh) f.driftY = -Math.abs(f.driftY);
            if (f.y < -hh) f.driftY =  Math.abs(f.driftY);
          }
        }

        // --- Particle update ---
        let attrDirty = false;

        for (let i = 0; i < n; i++) {
          if (captured[i]) continue; // skip captured (off-screen) particles
          const pi = i * 3;

          const turbX = (Math.random() - 0.5) * cfg.turbulence;
          const turbY = (Math.random() - 0.5) * cfg.turbulence * 0.25;

          let stepX = (vx[i] * gust + turbX) * dt;
          let stepY = (vy[i] + turbY) * dt;

          // ---- Thermodynamic flow fields ----
          // Each thermal combines three physical effects:
          //   1. Gaussian updraft/downdraft (vertical thermal buoyancy)
          //   2. Convergence (hot) / Divergence (cold) toward/from center
          //   3. Rankine vortex (irrotational outside core, solid-body inside)
          if (flowsOn) {
            for (let fi = 0; fi < numF; fi++) {
              const f   = flows[fi];
              const fdx = pos[pi]   - f.x;
              const fdy = pos[pi+1] - f.y;
              const fr2 = fdx * fdx + fdy * fdy;

              if (fr2 > R2skip) continue; // negligible Gaussian, skip

              // Gaussian envelope: exp(-r²/σ²) with σ = R/√2
              const g = Math.exp(-fr2 * R2i);

              // 1. Thermal buoyancy — vertical only
              stepY += f.sign * fStr * g * dt;

              if (fr2 > 0.0001) {
                const fr  = Math.sqrt(fr2);
                const fri = 1 / fr;

                // 2. Convergence (hot, sign=+1 → pulls in) / Divergence (cold → pushes out)
                //    F = −sign × 0.35 × fStr × g  in the radial direction
                const conv = -f.sign * fStr * 0.35 * g;
                stepX += fdx * fri * conv * dt;
                stepY += fdy * fri * conv * dt;

                // 3. Rankine vortex — tangential velocity ⊥ radius
                //    Core  (r < rc): v_θ = vStr × r/rc      (solid-body rotation)
                //    Outer (r ≥ rc): v_θ = vStr × rc/r      (irrotational, ∝ 1/r)
                //    Direction CCW: (-dy/r, dx/r) × vortexSign
                const vt = f.vortexSign * vStr
                  * (fr < rc ? fr / rc : rc / fr);
                stepX += -fdy * fri * vt * dt;
                stepY +=  fdx * fri * vt * dt;
              }
            }
          }

          // ---- Mouse influence ----
          // Outer ring (mr … 1.8×mr): swirl + velocity drag with C¹ smoothstep falloff.
          // Hard boundary: elastic reflection off circle; safety push ejects stray particles.
          if (mouseOn) {
            const dx     = pos[pi]   - mx;
            const dy     = pos[pi+1] - my;
            const d2     = dx * dx + dy * dy;
            const r      = Math.sqrt(d2);
            const outerR = mr * 1.8;

            if (r > mr && r < outerR) {
              const fri   = 1 / r;
              const t     = 1 - (r - mr) / (outerR - mr);
              const soft  = t * t * (3 - 2 * t);
              const fMag  = soft * cfg.mouseStrength * 0.4 * dt;
              const swirl = cfg.mouseSwirlRatio;
              stepX += (-dy) * fri * fMag * swirl;
              stepY +=   dx  * fri * fMag * swirl;
              stepX += mvx * soft * 0.45 * dt;
              stepY += mvy * soft * 0.45 * dt;
            }

            // Hard collision: reflect this frame's full step off circle boundary.
            // stepX/stepY already contains all forces (wind, turbulence, flows),
            // so the reflection is force-aware. vx[i]/vy[i] are NOT modified —
            // that would make the bounce persist across frames.
            const newX = pos[pi]   + stepX;
            const newY = pos[pi+1] + stepY;
            const ndx  = newX - mx;
            const ndy  = newY - my;
            const nd2  = ndx * ndx + ndy * ndy;
            if (nd2 < mr2 && nd2 > 0.0001) {
              const nd  = Math.sqrt(nd2);
              const nx  = ndx / nd;
              const ny  = ndy / nd;
              const e   = cfg.mouseBounce;
              const dot = stepX * nx + stepY * ny;
              if (dot < 0) {
                stepX -= (1 + e) * dot * nx;
                stepY -= (1 + e) * dot * ny;
              }
            }
          }

          pos[pi]   += stepX;
          pos[pi+1] += stepY;

          // Safety push: eject particle if it ended up inside the cursor circle
          if (mouseOn) {
            const ex  = pos[pi]   - mx;
            const ey  = pos[pi+1] - my;
            const ed2 = ex * ex + ey * ey;
            if (ed2 < mr2 && ed2 > 0.0001) {
              const ed  = Math.sqrt(ed2);
              pos[pi]   = mx + (ex / ed) * (mr + 0.5);
              pos[pi+1] = my + (ey / ed) * (mr + 0.5);
            }
          }

          // ---- Easter egg: black hole orbit capture ----
          // A particle is captured after completing one full orbit (2π radians)
          // around the cursor. Tracking is only active within TRACK_R.
          // The accumulated angle resets when the particle escapes beyond RESET_R.
          if (cfg.easterEggEnabled && mouseOn) {
            const odx  = pos[pi]   - mx;
            const ody  = pos[pi+1] - my;
            const or   = Math.sqrt(odx * odx + ody * ody);
            const TRACK_R = mr * 2.5;
            const RESET_R = mr * 4.0;
            if (or < TRACK_R) {
              const ang = Math.atan2(ody, odx);
              if (orbitInZone[i]) {
                let dA = ang - orbitAngle[i];
                if (dA >  Math.PI) dA -= 2 * Math.PI; // wrap to [-π, π]
                if (dA < -Math.PI) dA += 2 * Math.PI;
                orbitAcc[i] += dA;
                if (Math.abs(orbitAcc[i]) >= 2 * Math.PI) {
                  captured[i]  = 1;
                  spawnCross(pos[pi], pos[pi+1]);
                  pos[pi]      = -1e6; // move off-screen (invisible)
                  pos[pi+1]    = -1e6;
                  attrDirty    = true;
                  capturedCount++;
                  if (!allCapturedFired && capturedCount >= n) {
                    allCapturedFired = true;
                    onAllCaptured?.();
                  }
                  continue; // skip wrap logic for this particle
                }
              } else {
                orbitInZone[i] = 1;
                orbitAcc[i]    = 0;
              }
              orbitAngle[i] = ang;
            } else if (or > RESET_R) {
              orbitInZone[i] = 0;
              orbitAcc[i]    = 0;
            }
          }

          // Horizontal wrap (right → left); regenerate particle from current config
          if (pos[pi] > w / 2 + 8) {
            initParticle(i, cfg, false);
            attrDirty = true;
          }
          // Vertical wrap
          if (pos[pi+1] >  h / 2) pos[pi+1] = -h / 2;
          if (pos[pi+1] < -h / 2) pos[pi+1] =  h / 2;
        }

        geo.attributes.position.needsUpdate = true;
        if (attrDirty) {
          geo.attributes.aSize.needsUpdate  = true;
          geo.attributes.aAlpha.needsUpdate = true;
        }

        // Cross fade update
        let crossDirty = false;
        for (let s = 0; s < n; s++) {
          if (!crossLive[s]) continue;
          crossAlpha[s] = Math.max(0, crossAlpha[s] - CROSS_FADE * dt);
          const bi = s * CROSS_PTS;
          const a  = crossAlpha[s];
          for (let k = 0; k < CROSS_PTS; k++) crossAl[bi + k] = a;
          if (crossAlpha[s] <= 0) {
            crossLive[s] = 0;
            crossFree[crossFreeTop++] = s;
          }
          crossDirty = true;
        }
        if (crossDirty) {
          geoX.attributes.position.needsUpdate = true;
          geoX.attributes.aAlpha.needsUpdate   = true;
        }

        renderer.render(scene, camera);

        const workMs = performance.now() - t0;

        if (onStats && frameN % 20 === 0) {
          const fps = fpsBuf.length > 1
            ? Math.round(1000 * fpsBuf.length / (now - fpsBuf[0]))
            : 0;
          onStats({ fps, frameMs: workMs.toFixed(2), aliveCount: n - capturedCount, particleCount: n });
        }
      }

      animId = requestAnimationFrame(tick);

      function onResize() {
        w = el.clientWidth;
        h = el.clientHeight;
        renderer.setSize(w, h);
        camera.left   = -w / 2;
        camera.right  =  w / 2;
        camera.top    =  h / 2;
        camera.bottom = -h / 2;
        camera.updateProjectionMatrix();
      }
      window.addEventListener('resize', onResize);

      cleanup = () => {
        if (resetRef) resetRef.current = null;
        cancelAnimationFrame(animId);
        window.removeEventListener('resize', onResize);
        geo.dispose();
        mat.dispose();
        geoX.dispose();
        matX.dispose();
        renderer.dispose();
        if (el.contains(renderer.domElement)) el.removeChild(renderer.domElement);
      };
    });

    return () => {
      active = false;
      cleanup();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      ref={mountRef}
      aria-hidden="true"
      className="absolute inset-0 pointer-events-none"
      style={{ zIndex: 1 }}
    />
  );
}
